/**
 * ShareChatModal — Iter 153.43 (Feb 9, 2026, Yash).
 *
 * Two share modes:
 *   (a) Public read-only link — generates a tokenised URL valid for N days.
 *   (b) Email the chat export to one or more recipients.
 */
import { useState } from "react";
import { Copy, Link2, Loader2, Mail, X } from "lucide-react";
import { leadsApi } from "../../lib/api";
import { toast } from "sonner";

export default function ShareChatModal({ lead, onClose }) {
  const [mode, setMode] = useState("link"); // 'link' | 'email'
  const [expiresIn, setExpiresIn] = useState(7);
  const [includeNotes, setIncludeNotes] = useState(false);
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);
  // Email mode
  const [emailTo, setEmailTo] = useState("");
  const [emailFmt, setEmailFmt] = useState("pdf");
  const [emailNote, setEmailNote] = useState("");

  if (!lead) return null;

  const handleCreateLink = async () => {
    setBusy(true);
    try {
      const res = await leadsApi.createShare(lead.id, Number(expiresIn) || 7, includeNotes);
      setLink(res.data);
      toast.success("Share link created");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create share link");
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy — please copy manually");
    }
  };

  const handleEmail = async () => {
    const recipients = emailTo.split(",").map(s => s.trim()).filter(Boolean);
    if (recipients.length === 0) {
      toast.error("Add at least one recipient email");
      return;
    }
    setBusy(true);
    try {
      await leadsApi.emailExport(lead.id, {
        to: recipients,
        format: emailFmt,
        note: emailNote,
      });
      toast.success(`Sent to ${recipients.join(", ")}`);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Email send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4 animate-fade-in" data-testid="share-chat-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-[--border]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[--border]">
          <div>
            <h2 className="text-[15px] font-bold text-[--text-primary]" style={{ fontFamily: "Outfit, sans-serif" }}>Share chat</h2>
            <p className="text-[11px] text-[--text-muted] mt-0.5">
              {lead.name || "(no name)"} · +{lead.phone}
            </p>
          </div>
          <button onClick={onClose} className="btn-icon" data-testid="share-close-btn"><X size={16} /></button>
        </div>

        {/* Mode tabs */}
        <div className="flex gap-1 px-5 pt-4">
          <button
            onClick={() => setMode("link")}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-bold transition-all ${mode === "link" ? "bg-[--accent-pale] text-[--accent] border border-[--accent]/30" : "border border-[--border] text-[--text-muted] hover:text-[--text-primary]"}`}
            data-testid="share-mode-link"
          >
            <Link2 size={13} /> Public link
          </button>
          <button
            onClick={() => setMode("email")}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-bold transition-all ${mode === "email" ? "bg-[--accent-pale] text-[--accent] border border-[--accent]/30" : "border border-[--border] text-[--text-muted] hover:text-[--text-primary]"}`}
            data-testid="share-mode-email"
          >
            <Mail size={13} /> Email
          </button>
        </div>

        {mode === "link" ? (
          <div className="p-5 space-y-3">
            {!link ? (
              <>
                <div>
                  <label className="text-[11px] font-bold text-[--text-muted] uppercase tracking-wider">Expires after</label>
                  <select
                    value={expiresIn}
                    onChange={(e) => setExpiresIn(Number(e.target.value))}
                    className="w-full mt-1 px-3 py-2 border border-[--border] rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-[--accent]"
                    data-testid="share-expires-select"
                  >
                    <option value={1}>1 day</option>
                    <option value={7}>7 days</option>
                    <option value={30}>30 days</option>
                    <option value={90}>90 days</option>
                  </select>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeNotes}
                    onChange={(e) => setIncludeNotes(e.target.checked)}
                    data-testid="share-include-notes"
                  />
                  <span className="text-[12px] text-[--text-secondary]">Include internal notes</span>
                </label>
                <p className="text-[11px] text-[--text-muted] leading-relaxed">
                  Anyone with this link can view the chat read-only until it expires. You can revoke it any time from the lead's history.
                </p>
                <button
                  onClick={handleCreateLink}
                  disabled={busy}
                  className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-[--accent] text-white text-[13px] font-bold hover:opacity-90 transition-all disabled:opacity-50"
                  data-testid="share-create-link-btn"
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
                  Create link
                </button>
              </>
            ) : (
              <>
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-[11px] font-bold text-green-700 uppercase tracking-wider mb-1">Share link ready</p>
                  <p className="text-[10px] text-[--text-muted] mb-2">
                    Expires {new Date(link.expires_at).toLocaleString()}
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={link.url}
                      className="flex-1 px-2 py-1.5 bg-white border border-green-200 rounded text-[11px] font-mono"
                      data-testid="share-link-url"
                    />
                    <button
                      onClick={handleCopy}
                      className="px-3 py-1.5 rounded bg-green-600 text-white text-[11px] font-bold hover:bg-green-700"
                      data-testid="share-copy-btn"
                    >
                      <Copy size={12} className="inline-block mr-1" /> Copy
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => setLink(null)}
                  className="w-full px-3 py-2 text-[12px] font-bold text-[--text-muted] hover:text-[--text-primary] transition-all"
                >
                  Create another link
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="p-5 space-y-3">
            <div>
              <label className="text-[11px] font-bold text-[--text-muted] uppercase tracking-wider">Recipients</label>
              <input
                type="text"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="a@example.com, b@example.com"
                className="w-full mt-1 px-3 py-2 border border-[--border] rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-[--accent]"
                data-testid="share-email-to"
              />
              <p className="text-[10px] text-[--text-muted] mt-1">Comma-separated for multiple</p>
            </div>
            <div>
              <label className="text-[11px] font-bold text-[--text-muted] uppercase tracking-wider">Format</label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {["pdf", "csv", "txt"].map(f => (
                  <button
                    key={f}
                    onClick={() => setEmailFmt(f)}
                    className={`px-3 py-2 rounded-lg text-[12px] font-bold transition-all ${emailFmt === f ? "bg-[--accent-pale] text-[--accent] border border-[--accent]/30" : "border border-[--border] text-[--text-muted]"}`}
                    data-testid={`share-email-fmt-${f}`}
                  >
                    {f.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[11px] font-bold text-[--text-muted] uppercase tracking-wider">Note (optional)</label>
              <textarea
                value={emailNote}
                onChange={(e) => setEmailNote(e.target.value)}
                rows={2}
                placeholder="Short message for the recipient"
                className="w-full mt-1 px-3 py-2 border border-[--border] rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-[--accent] resize-none"
                data-testid="share-email-note"
              />
            </div>
            <button
              onClick={handleEmail}
              disabled={busy || !emailTo.trim()}
              className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-[--accent] text-white text-[13px] font-bold hover:opacity-90 transition-all disabled:opacity-50"
              data-testid="share-send-email-btn"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
              Send email
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
